import { useUser } from '@clerk/clerk-react';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDownIcon, Loader2Icon } from 'lucide-react';

import GithubLogo from '@/assets/icons/github.svg?react';
import approachingMesa from '@/assets/onboarding-2.webp';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { installationUrl } from '@/lib/installation';


export const Route = createFileRoute('/_authed/install')({
  component: RouteComponent,
});

function RouteComponent() {
  const { user } = useUser();

  const installMesa = async () => {
    window.location.href = installationUrl();
  };

  return (
    <div
      className="flex items-center justify-center h-full"
      style={{
        backgroundImage: `url('${approachingMesa}')`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        backgroundColor: '#000',
      }}
    >
      <Card className="h-[80vh] max-h-[600px] w-[600px] overflow-auto">
        {!user && (
          <div className="flex items-center justify-center h-full">
            <Loader2Icon className="h-4 w-4 animate-spin" />
          </div>
        )}
        {user && (
          <>
            <CardHeader>
              <CardTitle>
                <h2 className="text-2xl font-bold text-stone-700">Welcome to the Mesa, {user.firstName}</h2>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-stone-700">
                We cannot wait to see what you build. Install Mesa into your GitHub account in order to get started.
              </p>
              <div className="flex items-center justify-center">
                <Button variant="outline" className="w-40 my-4 hover:bg-stone-100 text-stone-700" onClick={installMesa}>
                  <GithubLogo className="size-4 mr-2" /> Install Mesa
                </Button>
              </div>
              <p className="text-sm text-stone-600 mb-4">
                A brief explanation of the permissions we need and why. You can uninstall at any time and we will delete
                your data completely.
              </p>
              <div className="flex flex-col gap-2 overflow-auto">
                <Collapsible className="hover:bg-muted rounded-md">
                  <CollapsibleTrigger className="hover:cursor-pointer p-2">
                    <h3 className="flex items-center gap-2 font-medium text-sm">
                      Repository Administration (Read-Only)
                    </h3>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <p className="text-sm text-stone-600 pl-2 border-l-2">
                      Enables: list your teammates, tag them in comments, and request their review.
                    </p>
                  </CollapsibleContent>
                </Collapsible>
                <Collapsible className="hover:bg-muted rounded-md">
                  <CollapsibleTrigger className="hover:cursor-pointer p-2">
                    <h3 className="flex items-center gap-2 font-medium text-sm">Actions, Checks, Commit Statuses</h3>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <p className="text-sm text-stone-600 pl-2 border-l-2">
                      Enables: Realtime status of GitHub Actions and Checks on a PR, re-running of Checks and Actions.
                    </p>
                  </CollapsibleContent>
                </Collapsible>
                <Collapsible className="hover:bg-muted rounded-md">
                  <CollapsibleTrigger className="hover:cursor-pointer p-2">
                    <h3 className="flex items-center gap-2 font-medium text-sm">Contents, Metadata</h3>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <p className="text-sm text-stone-600 pl-2 border-l-2">
                      Enables: Render diffs of your PRs and provide you with branch + commit info.
                    </p>
                  </CollapsibleContent>
                </Collapsible>
                <Collapsible className="hover:bg-muted rounded-md">
                  <CollapsibleTrigger className="hover:cursor-pointer p-2">
                    <h3 className="flex items-center gap-2 font-medium text-sm">Pull Requests</h3>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <p className="text-sm text-stone-600 pl-2 border-l-2">
                      Enables: Read PR metadata such as title, description, comments, labels, and review and merge PRs.
                    </p>
                  </CollapsibleContent>
                </Collapsible>
                <Collapsible className="hover:bg-muted rounded-md">
                  <CollapsibleTrigger className="hover:cursor-pointer p-2">
                    <h3 className="flex items-center gap-2 font-medium text-sm">Email Address</h3>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <p className="text-sm text-stone-600 pl-2 border-l-2">
                      Enables: Know who you are when you sign-in to Mesa!
                    </p>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
